import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import { AddOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import React from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"

import {
  type OverrideTestResultsFormData,
  OverrideTestResultsSchema,
  type ProductTestDocumentFormValues,
  type ProductTestResultFormValues,
} from "../../schemas"

import { Button } from "@/components/common/Button"
import type { Product, ProductTestResult } from "@/graphql/codegen/graphql"
import { ProductTestResultStatus } from "@/graphql/codegen/graphql"
import OverrideTestResultsTable from "@/screens/Products/components/Product/Quality/TestResults/components/OverrideTestResultsDialog/OverrideTestResultsTable"
import { ProductTestDocumentsForm } from "@/screens/Products/components/Product/Quality/TestResults/components/ProductTestDocumentsForm"
import { ProductTestVersionUpdateDialog } from "@/screens/Products/components/Product/Quality/TestResults/components/ProductTestVersionUpdateDialog"
import { useProductTestFormSubmit } from "@/screens/Products/components/Product/Quality/TestResults/hooks"
import {
  ProductTestDiffType,
  type ProductTestWithDocuments,
} from "@/screens/Products/components/Product/Quality/TestResults/types"

interface OverrideTestResultsDialogProps {
  onClose: () => void
  productTestResult: ProductTestResult | null
  productTest?: ProductTestWithDocuments | null
  product: Product
  onSubmit: () => void
}

const EMPTY_FILE = {
  type: "",
  description: "",
  file: null,
} as ProductTestDocumentFormValues

const OverrideTestResultsDialog: React.FC<OverrideTestResultsDialogProps> = ({
  onClose,
  productTestResult,
  productTest,
  product,
  onSubmit,
}) => {
  const { handleUpdate } = useProductTestFormSubmit({
    product,
    productTestDocuments: productTest?.documents ?? [],
    onSubmit,
  })
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const methods = useForm<OverrideTestResultsFormData>({
    resolver: zodResolver(OverrideTestResultsSchema),
    defaultValues: {
      documents: [EMPTY_FILE],
    },
  })
  const { control, watch, reset, formState } = methods
  const { fields, append: appendNewDocument } = useFieldArray({
    control,
    name: "documents",
  })
  const documents = watch("documents")
  const showAddButton = documents.every((field) => field.file !== null && !!field.type)

  const handleOpenConfirmationDialog = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsConfirmDialogOpen(true)
  }

  const handleOverrideResults = async (isLotUpdated: boolean) => {
    try {
      setIsSubmitting(true)
      const updatedTestResults =
        productTest?.results?.map((result) => {
          const isUpdatedResult = result.productTestResultId === productTestResult?.productTestResultId

          return {
            productTestResultId: result.productTestResultId,
            value: result.value,
            ...(isUpdatedResult && { status: ProductTestResultStatus.UpdatedResultsWithinSpec }),
          } as ProductTestResultFormValues
        }) || []

      await handleUpdate(
        {
          productTest: {
            results: updatedTestResults,
            manufacturingDate: productTest?.manufacturingDate ?? undefined,
            expirationDate: productTest?.expirationDate ?? undefined,
          },
          documentsToCreate: documents.filter((doc) => doc.file !== null && !!doc.type),
          documentsToDelete: [],
        },
        productTest?.productTestId ?? "",
        isLotUpdated
      )
    } finally {
      setIsSubmitting(false)
      setIsConfirmDialogOpen(false)
      handleClose()
    }
  }

  const handleClose = () => {
    reset()
    onClose?.()
  }

  return (
    <>
      <Dialog open onClose={handleClose} data-testid='override-test-results-dialog' maxWidth='sm' fullWidth>
        <FormProvider {...methods}>
          <form>
            <DialogContent className='flex flex-col gap-6 p-10'>
              <Box className='flex flex-col items-center gap-4'>
                <WarningOutlined className='size-[48px] text-red-600' />
                <Typography variant='h5' color='primary' align='center' className='text-2xl font-normal -tracking-wide'>
                  Override Test Results
                </Typography>
              </Box>

              <Typography
                color='textSecondary'
                variant='body1'
                align='center'
                className='mb-5 max-w-2xl text-base -tracking-wide'
              >
                The test results are outside the established limits. Would you like to proceed with approval anyway?
              </Typography>

              <OverrideTestResultsTable productTestResults={productTestResult} />
              <ProductTestDocumentsForm productTestDocuments={[]} title='Supporting Documents' fields={fields} />
            </DialogContent>

            <DialogActions className='justify-between px-10 pb-10 pt-0'>
              <Button data-testid='cancel-button' appearance='outlined' onClick={handleClose}>
                Cancel
              </Button>
              <Box>
                {showAddButton && (
                  <Button
                    appearance='text'
                    onClick={() => appendNewDocument(EMPTY_FILE)}
                    startIcon={<AddOutlined />}
                    data-testid='add-file-button'
                  >
                    Add Another File
                  </Button>
                )}
                <Button
                  data-testid='approve-button'
                  variant='primary'
                  type='submit'
                  onClick={handleOpenConfirmationDialog}
                  disabled={!formState.isValid}
                >
                  Approve Test
                </Button>
              </Box>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      {isConfirmDialogOpen && (
        <ProductTestVersionUpdateDialog
          onClose={() => setIsConfirmDialogOpen(false)}
          diffType={ProductTestDiffType.TestResults}
          onSubmit={handleOverrideResults}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  )
}

export default OverrideTestResultsDialog
